import { useEffect, useState, useMemo, useCallback } from 'react'
import { Plus, Network } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { useFitTheses } from '@/hooks/useFitTheses'
import { personaWorkshopApi } from '@/lib/api/client'
import { FitThesisMatrix, type PersonaSummary } from './FitThesisMatrix'
import { FitThesisDrawer } from './FitThesisDrawer'
import { FitThesisCanvasPeek } from './FitThesisCanvasPeek'
import { FitThesisPortfolioBar } from './FitThesisPortfolioBar'
import { NewThesisDialog } from './NewThesisDialog'
import type { FitThesis } from '@ispot/shared'

interface PersonaSessionRow {
  id: string
  personaType: string
  personaData: Record<string, unknown>
  status: string
}

/**
 * Normalise persona workshop data from either the new JTBD shape or the legacy flat shape.
 *
 * New (JTBD): `{ name, segment, jtbds: [{ id, label, pains: [...] }] }`
 * Legacy:     `{ name, role, goals: [...], pains: [...], archetype, segment }`
 *
 * For legacy rows, synthesise one JTBD per goal; if no goals exist, emit a single "default"
 * JTBD so the user can still start a thesis against this persona.
 */
function summarisePersona(s: PersonaSessionRow): PersonaSummary {
  const data = (s.personaData ?? {}) as {
    name?: string
    role?: string
    archetype?: string
    segment?: string
    photo?: string
    jtbds?: Array<{
      id: string
      title?: string
      label?: string
      statement?: string
      jtbd?: string
      description?: string
      functional?: string
      pains?: Array<{
        id: string
        label?: string
        statement?: string
        description?: string
        severity?: string
      }>
    }>
    goals?: unknown[]
    pains?: Array<{ id?: string; text?: string; label?: string; description?: string; intensity?: string; severity?: string }>
  }
  const name = data.name ?? data.archetype ?? data.role ?? 'Unnamed persona'
  const segment = data.segment ?? null
  const photo = typeof data.photo === 'string' && data.photo.trim() ? data.photo : undefined

  const jtbdList = Array.isArray(data.jtbds) ? data.jtbds : []
  if (jtbdList.length > 0) {
    return {
      sessionId: s.id,
      name,
      segment,
      photo,
      jtbds: jtbdList.map(j => ({
        id: j.id,
        label:
          j.title ??
          j.label ??
          j.statement ??
          j.jtbd ??
          j.functional ??
          j.description ??
          '(unnamed JTBD)',
        pains: Array.isArray(j.pains)
          ? j.pains.map(p => ({
              id: p.id,
              label: p.description ?? p.label ?? p.statement ?? '(unnamed pain)',
              severity: p.severity,
            }))
          : [],
      })),
    }
  }

  // Legacy shape: derive JTBDs from goals[]
  const goals = Array.isArray(data.goals) ? data.goals : []
  if (goals.length > 0) {
    return {
      sessionId: s.id,
      name,
      segment,
      photo,
      jtbds: goals.map((g, i) => ({
        id: `legacy-goal-${i}`,
        label: typeof g === 'string' ? g : String((g as any)?.text ?? `Goal ${i + 1}`),
      })),
    }
  }

  // No goals or jtbds: single fallback so the persona is still usable
  return {
    sessionId: s.id,
    name,
    segment,
    photo,
    jtbds: [{ id: 'default', label: 'General JTBD' }],
  }
}

export function FitThesisPage() {
  const account = useAuthStore(s => s.currentAccount)
  const project = useProjectStore(s => s.currentProject)

  const accountId = account?.id ?? ''
  const projectId = project?.id ?? ''

  const { theses, isLoading, error, createThesis, patchBlock, validateThesis, reload } = useFitTheses(accountId, projectId)

  const [personas, setPersonas] = useState<PersonaSummary[]>([])
  const [loadingPersonas, setLoadingPersonas] = useState(false)

  useEffect(() => {
    if (!accountId || !projectId) return
    let cancelled = false
    setLoadingPersonas(true)
    personaWorkshopApi.listSessions(accountId, projectId)
      .then((rows: any) => {
        if (cancelled) return
        const list: PersonaSessionRow[] = Array.isArray(rows) ? rows : (rows?.sessions ?? [])
        const summaries: PersonaSummary[] = list.map(s => summarisePersona(s))
        setPersonas(summaries)
      })
      .catch(() => { if (!cancelled) setPersonas([]) })
      .finally(() => { if (!cancelled) setLoadingPersonas(false) })
    return () => { cancelled = true }
  }, [accountId, projectId])

  const personaById = useMemo(
    () => new Map(personas.map(p => [p.sessionId, p])),
    [personas],
  )

  const [activeThesisId, setActiveThesisId] = useState<string | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [peekOpen, setPeekOpen] = useState(false)

  const activeThesis: FitThesis | null = theses.find(t => t.id === activeThesisId) ?? null

  const totalPairs = useMemo(
    () => personas.reduce((sum, p) => sum + p.jtbds.length, 0),
    [personas],
  )

  /**
   * Power-user accelerator: press `N` (or `n`) anywhere on the page to open
   * the new-thesis dialog. Skips when typing in inputs, the drawer is open,
   * or the dialog is already open.
   */
  const openNew = useCallback(() => {
    if (personas.length === 0 || activeThesisId || newOpen) return
    setNewOpen(true)
  }, [personas.length, activeThesisId, newOpen])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key !== 'n' && e.key !== 'N') return
      const t = e.target as HTMLElement | null
      const tag = t?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return
      e.preventDefault()
      openNew()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openNew])

  async function handleCreate(personaSessionId: string, jtbdId: string, painId?: string) {
    const created = await createThesis(personaSessionId, jtbdId, painId)
    setActiveThesisId(created.id)
    setNewOpen(false)
  }

  /**
   * Direct create from a matrix row. Reuses an existing thesis for
   * this (persona, jtbd) if one already exists (unique constraint at DB level,
   * but we short-circuit client-side for a snappier UX).
   */
  async function handleStartFromRow(personaSessionId: string, jtbdId: string) {
    const existing = theses.find(t => t.personaSessionId === personaSessionId && t.jtbdId === jtbdId)
    if (existing) {
      setActiveThesisId(existing.id)
      return
    }
    const created = await createThesis(personaSessionId, jtbdId)
    setActiveThesisId(created.id)
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-bg">
      <header className="flex items-start justify-between gap-6 bg-bg px-10 pb-6 pt-9 max-md:flex-col max-md:px-5 max-md:pt-6">
        <div className="flex flex-col gap-1">
          <h1 className="font-display text-heading font-semibold tracking-tight text-fg">Fit Thesis</h1>
          <p className="max-w-[60ch] text-body text-fg-secondary">
            Map why a customer chooses you, one persona × job at a time.{' '}
            <button
              type="button"
              onClick={() => setPeekOpen(p => !p)}
              className="border-b border-border-strong pb-px text-fg transition hover:border-accent hover:text-accent"
              aria-expanded={peekOpen}
            >
              {peekOpen ? 'Hide canvas' : 'See the 9-block canvas →'}
            </button>
          </p>
        </div>
        <button
          onClick={openNew}
          disabled={personas.length === 0}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-transparent px-3 text-caption font-medium text-fg-secondary transition hover:border-border-strong hover:bg-bg-card hover:text-fg disabled:cursor-not-allowed disabled:opacity-50"
          title={personas.length === 0 ? 'Create a persona first' : 'Start a new fit thesis (N)'}
        >
          <Plus className="h-3.5 w-3.5" />
          New thesis
          <kbd className="ml-1 inline-grid min-w-[18px] place-items-center rounded border border-border bg-bg-secondary px-1 font-mono text-[11px] font-medium text-fg-muted">
            N
          </kbd>
        </button>
      </header>

      <main className="flex-1 overflow-y-auto px-10 pb-16 max-md:px-5">
        {peekOpen && <FitThesisCanvasPeek onClose={() => setPeekOpen(false)} />}

        {error && (
          <div className="mb-6 rounded-md border border-danger-fg bg-danger-bg px-4 py-3 text-body text-danger-fg">
            {error}
          </div>
        )}

        {personas.length === 0 && !loadingPersonas && (
          <div className="py-16 text-center">
            <Network className="mx-auto mb-4 h-10 w-10 text-fg-muted" />
            <h2 className="font-display text-subhead font-semibold text-fg">No personas yet</h2>
            <p className="mx-auto mt-2 max-w-[52ch] text-body text-fg-secondary">
              A fit thesis is anchored on one persona and one JTBD. Create a persona in the workshop first, then come back here.
            </p>
          </div>
        )}

        {personas.length > 0 && (
          <>
            <FitThesisPortfolioBar theses={theses} totalPairs={totalPairs} />
            <FitThesisMatrix
              personas={personas}
              theses={theses}
              isLoading={isLoading || loadingPersonas}
              onOpenThesis={setActiveThesisId}
              onStart={handleStartFromRow}
            />
          </>
        )}
      </main>

      {activeThesis && (
        <FitThesisDrawer
          thesis={activeThesis}
          personas={personaById}
          accountId={accountId}
          projectId={projectId}
          onClose={() => { setActiveThesisId(null); reload() }}
          onBlockPatched={async (blockKey, value) => {
            await patchBlock(activeThesis.id, blockKey, value)
          }}
          onValidate={async () => { await validateThesis(activeThesis.id) }}
        />
      )}

      {newOpen && (
        <NewThesisDialog
          personas={personas}
          existing={theses}
          onCancel={() => setNewOpen(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  )
}
